import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import axios from '../api/axios';
const dogImg = require('../assets/dog1.jpg');


const Login: React.FC = () => {
  const navigate = useNavigate();

  // Login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  // Register modal state
  const [registerOpen, setRegisterOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [phone, setPhone] = useState('');
  // Removed address fields
  const [regPassword, setRegPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [registerError, setRegisterError] = useState('');
  const [registerSuccessOpen, setRegisterSuccessOpen] = useState(false);
  // Verification modal state
  const [verificationOpen, setVerificationOpen] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationError, setVerificationError] = useState('');

  // Forgot password state
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [resetOpen, setResetOpen] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resetError, setResetError] = useState('');

  // Loading state
  const [loading, setLoading] = useState(false);

  // Helpers
  const validateEmail = (e: string) => /^[\w.+-]+@[\w-]+(\.[\w-]+)+$/.test(e);

  const handleLogin = async () => {
    setLoginError('');
    if (!email || !password) {
      setLoginError('Email and password are required.');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post('/user/login', { email, password });
      // Expect backend to return auth token / user info
      // Save token as appropriate (example using localStorage)
      if (res.data?.token) localStorage.setItem('token', res.data.token);
      if (typeof res.data?.isAdmin !== 'undefined') {
        localStorage.setItem('isAdmin', res.data.isAdmin ? 'true' : 'false');
      }
      navigate('/');
    } catch (err) {
      setLoginError('Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRegister = () => {
    setRegisterError('');
    setFirstName('');
    setLastName('');
    setRegEmail('');
    setPhone('');
    // Removed address fields
    setRegPassword('');
    setConfirmPassword('');
    setRegisterOpen(true);
  };

  // Step 1: Send verification code
  const handleRegister = async () => {
    setRegisterError('');
    // Basic validations
    if (!firstName || !lastName || !regEmail || !phone || !regPassword || !confirmPassword) {
      setRegisterError('All fields are required.');
      return;
    }
    if (!validateEmail(regEmail)) {
      setRegisterError('Please enter a valid email address.');
      return;
    }
    if (regPassword.length < 12) {
      setRegisterError('Password must be at least 12 characters.');
      return;
    }
    if (regPassword !== confirmPassword) {
      setRegisterError('Passwords do not match.');
      return;
    }

    try {
      await axios.post('/user/sendverification', { email: regEmail });
      setRegisterOpen(false);
      setVerificationOpen(true);
      setVerificationError('');
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || '';
      if (msg.toLowerCase().includes('registration failed')) {
        setRegisterError('Registration failed. Please check your information and try again.');
      } else {
        setRegisterError('Failed to send verification email.');
      }
    }
  };

  // Step 2: Submit registration with verification code
  const handleVerifyAndRegister = async () => {
    setVerificationError('');
    if (!verificationCode) {
      setVerificationError('Please enter the verification code.');
      return;
    }
    try {
      const payload = {
        fname: firstName,
        lname: lastName,
        email: regEmail,
        phone,
        password: regPassword,
        verificationCode
      };
      await axios.post('/user/register', payload);
      setVerificationOpen(false);
      setRegisterSuccessOpen(true);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || '';
      if (msg.toLowerCase().includes('registration failed')) {
        setVerificationError('Registration failed. Please check your information and try again.');
      } else {
        setVerificationError('Registration failed. Please check your code and try again.');
      }
    }
  };

  // Forgot password handlers
  const handleForgotOpen = () => {
    setForgotEmail('');
    setForgotError('');
    setForgotOpen(true);
  };

  const handleForgotSubmit = async () => {
    setForgotError('');
    if (!forgotEmail) {
      setForgotError('Please enter your email address.');
      return;
    }
    if (!validateEmail(forgotEmail)) {
      setForgotError('Please enter a valid email address.');
      return;
    }
    try {
      await axios.post('/user/sendverification', { email: forgotEmail, passwordReset: true });
      setForgotOpen(false);
      setResetOpen(true);
      setResetCode('');
      setResetPassword('');
      setResetConfirmPassword('');
      setResetError('');
    } catch (err) {
      setForgotError('Failed to send password reset email.');
    }
  };

  const handleResetSubmit = async () => {
    setResetError('');
    if (!resetCode || !resetPassword || !resetConfirmPassword) {
      setResetError('All fields are required.');
      return;
    }
    if (resetPassword.length < 12) {
      setResetError('Password must be at least 12 characters.');
      return;
    }
    if (resetPassword !== resetConfirmPassword) {
      setResetError('Passwords do not match.');
      return;
    }
    try {
      await axios.post('/user/resetpassword', {
        email: forgotEmail,
        verificationCode: resetCode,
        password: resetPassword
      });
      setResetOpen(false);
      // Optionally show a snackbar or alert here
      // Redirect to login
      navigate('/');
    } catch (err) {
      setResetError('Password reset failed. Please check your code and try again.');
    }
  };

  return (
    <>
      <Box sx={{
        minHeight: '100vh',
        width: '100vw',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: -1,
        background: 'linear-gradient(135deg, #e0eafc 0%, #cfdef3 100%)',
      }} />
      <Container maxWidth="xs" sx={{ mt: 8, position: 'relative', minHeight: 700, zIndex: 1 }}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mb: 3,
          bgcolor: 'rgba(255,255,255,0.95)',
          borderRadius: 4,
          boxShadow: 4,
          px: 3,
          py: 4,
        }}>
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mb: 2 }}>
            <img src={dogImg} alt="Dog" style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', boxShadow: '0 2px 12px #aaa' }} onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = 'https://images.unsplash.com/photo-1518717758536-85ae29035b6d?auto=format&fit=facearea&w=400&h=400&q=80'; }} />
          </Box>
          <Typography variant="h4" align="center" sx={{ fontWeight: 900, color: 'primary.main', letterSpacing: 1, mb: 2, textShadow: '0 2px 8px #e3eafc' }}>
            Welcome to RVA Raw Food Co-Op
          </Typography>

          {loginError && <Alert severity="error" sx={{ mb: 2 }}>{loginError}</Alert>}

          <form
            onSubmit={e => {
              e.preventDefault();
              handleLogin();
            }}
            style={{ width: '100%' }}
            autoComplete="on"
          >
            <TextField
              label="Email"
              type="email"
              fullWidth
              sx={{ mb: 2 }}
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="username"
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              sx={{ mb: 2 }}
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
            />
            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mb: 1, position: 'relative' }}
              type="submit"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" sx={{ position: 'absolute', left: '50%', top: '50%', marginTop: '-12px', marginLeft: '-12px' }} /> : ''}{'Sign In'}
            </Button>
          </form>

          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1} width="100%">
            <Link component="button" variant="body2" onClick={handleOpenRegister}>Register</Link>
            <Link component="button" variant="body2" onClick={handleForgotOpen}>Forgot password?</Link>
          </Box>


          {/* Register Modal */}
          {/* Forgot Password Modal */}
          <Dialog open={forgotOpen} onClose={() => setForgotOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ bgcolor: '#fff' }}>Forgot Password</DialogTitle>
            <DialogContent sx={{ bgcolor: '#fff' }}>
              <Typography sx={{ mb: 2 }}>
                Enter your email address to receive a password reset code.
              </Typography>
              <TextField
                label="Email"
                type="email"
                fullWidth
                value={forgotEmail}
                onChange={e => setForgotEmail(e.target.value)}
                sx={{ mb: 2 }}
              />
              {forgotError && <Alert severity="error">{forgotError}</Alert>}
            </DialogContent>
            <DialogActions sx={{ bgcolor: '#fff' }}>
              <Button onClick={() => setForgotOpen(false)} color="inherit">Cancel</Button>
              <Button onClick={handleForgotSubmit} variant="contained" color="primary">Submit</Button>
            </DialogActions>
          </Dialog>

          {/* Password Reset Modal */}
          <Dialog open={resetOpen} onClose={() => setResetOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ bgcolor: '#fff' }}>Reset Password</DialogTitle>
            <DialogContent sx={{ bgcolor: '#fff' }}>
              <TextField
                label="Password Reset Code"
                fullWidth
                value={resetCode}
                onChange={e => setResetCode(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                label="New Password"
                type="password"
                fullWidth
                value={resetPassword}
                onChange={e => setResetPassword(e.target.value)}
                sx={{ mb: 2 }}
                helperText="At least 12 characters"
              />
              <TextField
                label="Confirm New Password"
                type="password"
                fullWidth
                value={resetConfirmPassword}
                onChange={e => setResetConfirmPassword(e.target.value)}
                sx={{ mb: 2 }}
              />
              {resetError && <Alert severity="error">{resetError}</Alert>}
            </DialogContent>
            <DialogActions sx={{ bgcolor: '#fff' }}>
              <Button onClick={() => setResetOpen(false)} color="inherit">Cancel</Button>
              <Button onClick={handleResetSubmit} variant="contained" color="primary">Submit</Button>
            </DialogActions>
          </Dialog>
          <Dialog open={registerOpen} onClose={() => setRegisterOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ bgcolor: '#fff' }}>Register</DialogTitle>
            <DialogContent sx={{ bgcolor: '#fff' }}>
              <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2} sx={{ pt: 1 }}>
                <Box>
                  <TextField label="First Name" fullWidth value={firstName} onChange={e => setFirstName(e.target.value)} />
                </Box>
                <Box>
                  <TextField label="Last Name" fullWidth value={lastName} onChange={e => setLastName(e.target.value)} />
                </Box>
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <TextField label="Email" type="email" fullWidth value={regEmail} onChange={e => setRegEmail(e.target.value)} />
                </Box>
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <TextField label="Phone" fullWidth value={phone} onChange={e => setPhone(e.target.value)} />
                </Box>
                {/* Address fields removed */}
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <TextField label="Password" type="password" fullWidth value={regPassword} onChange={e => setRegPassword(e.target.value)} helperText="At least 12 characters" />
                </Box>
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <TextField label="Confirm Password" type="password" fullWidth value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
                </Box>
              </Box>

              {registerError && <Alert severity="error" sx={{ mt: 2 }}>{registerError}</Alert>}
            </DialogContent>
            <DialogActions sx={{ bgcolor: '#fff' }}>
              <Button onClick={() => setRegisterOpen(false)} color="inherit">Cancel</Button>
              <Button onClick={handleRegister} variant="contained" color="primary">Register</Button>
            </DialogActions>
          </Dialog>

          {/* Verification Code Modal */}
          <Dialog open={verificationOpen} onClose={() => setVerificationOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ bgcolor: '#fff' }}>Email Verification</DialogTitle>
            <DialogContent sx={{ bgcolor: '#fff' }}>
              <Typography sx={{ mb: 2 }}>
                Please enter the verification code sent to your email address.
              </Typography>
              <TextField
                label="Verification Code"
                fullWidth
                value={verificationCode}
                onChange={e => setVerificationCode(e.target.value)}
                sx={{ mb: 2 }}
              />
              {verificationError && <Alert severity="error">{verificationError}</Alert>}
            </DialogContent>
            <DialogActions sx={{ bgcolor: '#fff' }}>
              <Button onClick={() => setVerificationOpen(false)} color="inherit">Cancel</Button>
              <Button onClick={handleVerifyAndRegister} variant="contained" color="primary">Submit</Button>
            </DialogActions>
          </Dialog>

          <Snackbar open={registerSuccessOpen} autoHideDuration={4000} onClose={() => setRegisterSuccessOpen(false)}>
            <Alert severity="success" onClose={() => setRegisterSuccessOpen(false)} sx={{ width: '100%' }}>
              Registration successful.
            </Alert>
          </Snackbar>
        </Box>
      </Container>
    </>
  );
};

export default Login;
